import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { message } from 'ant-design-vue'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'

// Configure NProgress
NProgress.configure({ showSpinner: false })

const router = createRouter({
  history: createWebHistory(),
  routes: [
    // ============ 公开页（无需登录，挂在无导航/页头的 PublicLayout 下） ============
    {
      path: '/',
      name: 'PublicLayout',
      component: () => import('@/components/PublicLayout.vue'),
      meta: { requiresAuth: false },
      // 守卫按「任一匹配记录未显式豁免即需登录」判断，子路由必须同样显式声明 requiresAuth: false
      children: [
        {
          path: '',
          name: 'Home',
          component: () => import('@/views/public/Home.vue'),
          meta: { requiresAuth: false, title: '首页' }
        },
        {
          path: 'about',
          name: 'About',
          component: () => import('@/views/public/About.vue'),
          meta: { requiresAuth: false, title: '关于' }
        }
      ]
    },

    // ============ 管理后台（/admin 前缀，需登录认证） ============
    {
      path: '/admin/login',
      name: 'Login',
      component: () => import('@/views/Login.vue'),
      meta: { requiresAuth: false, title: '登录' }
    },
    {
      path: '/admin/register',
      name: 'Register',
      component: () => import('@/views/Register.vue'),
      meta: { requiresAuth: false, title: '注册' }
    },
    {
      path: '/admin',
      name: 'Layout',
      component: () => import('@/components/Layout.vue'),
      meta: { requiresAuth: true },
      // 子路由统一使用相对路径，由父级 /admin 提供前缀，避免逐条硬编码
      children: [
        {
          path: '',
          name: 'Dashboard',
          component: () => import('@/views/Dashboard.vue'),
          meta: { title: '仪表盘', permission: 'dashboard:view' }
        },
        {
          path: 'sns/cigars/brand',
          name: 'CigarBrandList',
          component: () => import('@/views/sns/cigars/brand/index.vue'),
          meta: { title: '雪茄品牌管理', permission: 'sns:cigar:read' }
        },
        {
          path: 'sns/cigars/brand/category',
          name: 'CigarBrandCategory',
          component: () => import('@/views/sns/cigars/brand/category/index.vue'),
          meta: { title: '品牌分类管理', permission: 'sns:cigar:read' }
        },
        {
          path: 'sns/cigars/brand/category/create',
          name: 'CigarBrandCategoryCreate',
          component: () => import('@/views/sns/cigars/brand/category/create.vue'),
          meta: { title: '新增品牌分类', permission: 'sns:cigar:create' }
        },
        {
          path: 'sns/cigars/brand/category/edit/:id',
          name: 'CigarBrandCategoryEdit',
          component: () => import('@/views/sns/cigars/brand/category/edit.vue'),
          meta: { title: '编辑品牌分类', permission: 'sns:cigar:edit' }
        },
        {
          path: 'sns/cigars/brand/create',
          name: 'CigarBrandCreate',
          component: () => import('@/views/sns/cigars/brand/create.vue'),
          meta: { title: '新增品牌', permission: 'sns:cigar:create' }
        },
        {
          path: 'sns/cigars/brand/edit/:id',
          name: 'CigarBrandEdit',
          component: () => import('@/views/sns/cigars/brand/edit.vue'),
          meta: { title: '编辑品牌', permission: 'sns:cigar:edit' }
        },
        {
          path: 'sns/cigars/ring/create',
          name: 'CigarRingCreate',
          component: () => import('@/views/sns/cigars/ring/create.vue'),
          meta: { title: '新增环标', permission: 'sns:cigar:create' }
        },
        {
          path: 'sns/cigars/ring/edit/:id',
          name: 'CigarRingEdit',
          component: () => import('@/views/sns/cigars/ring/edit.vue'),
          meta: { title: '编辑环标', permission: 'sns:cigar:edit' }
        },
        {
          path: 'sns/cigars/serie',
          name: 'CigarSerieList',
          component: () => import('@/views/sns/cigars/serie/index.vue'),
          meta: { title: '雪茄系列管理', permission: 'sns:cigar:read' }
        },
        {
          path: 'sns/cigars/serie/create',
          name: 'CigarSerieCreate',
          component: () => import('@/views/sns/cigars/serie/create.vue'),
          meta: { title: '新增系列', permission: 'sns:cigar:create' }
        },
        {
          path: 'sns/cigars/serie/edit/:id',
          name: 'CigarSerieEdit',
          component: () => import('@/views/sns/cigars/serie/edit.vue'),
          meta: { title: '编辑系列', permission: 'sns:cigar:edit' }
        },
        {
          path: 'sns/cigars/commonName',
          name: 'CigarCommonNameList',
          component: () => import('@/views/sns/cigars/commonName/index.vue'),
          meta: { title: '雪茄通用名管理', permission: 'sns:cigar:read' }
        },
        {
          path: 'sns/cigars/commonName/create',
          name: 'CigarCommonNameCreate',
          component: () => import('@/views/sns/cigars/commonName/create.vue'),
          meta: { title: '新增通用名', permission: 'sns:cigar:create' }
        },
        {
          path: 'sns/cigars/commonName/edit/:id',
          name: 'CigarCommonNameEdit',
          component: () => import('@/views/sns/cigars/commonName/edit.vue'),
          meta: { title: '编辑通用名', permission: 'sns:cigar:edit' }
        },
        {
          path: 'sns/cigars/factoryVitola',
          name: 'CigarFactoryVitolaList',
          component: () => import('@/views/sns/cigars/factoryVitola/index.vue'),
          meta: { title: '雪茄厂用名管理', permission: 'sns:cigar:read' }
        },
        {
          path: 'sns/cigars/factoryVitola/create',
          name: 'CigarFactoryVitolaCreate',
          component: () => import('@/views/sns/cigars/factoryVitola/create.vue'),
          meta: { title: '新增厂用名', permission: 'sns:cigar:create' }
        },
        {
          path: 'sns/cigars/factoryVitola/edit/:id',
          name: 'CigarFactoryVitolaEdit',
          component: () => import('@/views/sns/cigars/factoryVitola/edit.vue'),
          meta: { title: '编辑厂用名', permission: 'sns:cigar:edit' }
        },
        {
          path: 'sns/cigars',
          name: 'CigarList',
          component: () => import('@/views/sns/cigars/cigar/index.vue'),
          meta: { title: '雪茄管理', permission: 'sns:cigar:read' }
        },
        {
          path: 'sns/cigars/identify',
          name: 'CigarIdentifyList',
          component: () => import('@/views/sns/cigars/identify/list.vue'),
          meta: { title: '雪茄鉴定规则', permission: 'sns:cigarIdentify:read' }
        },
        {
          path: 'sns/cigars/identify/create',
          name: 'CigarIdentifyCreate',
          component: () => import('@/views/sns/cigars/identify/create.vue'),
          meta: { title: '新增规则', permission: 'sns:cigarIdentify:create' }
        },
        {
          path: 'sns/cigars/identify/:id/edit',
          name: 'CigarIdentifyEdit',
          component: () => import('@/views/sns/cigars/identify/edit.vue'),
          meta: { title: '编辑规则', permission: 'sns:cigarIdentify:edit' }
        },
        {
          path: 'sns/cigars/create',
          name: 'CigarCreate',
          component: () => import('@/views/sns/cigars/cigar/create.vue'),
          meta: { title: '新增雪茄', permission: 'sns:cigar:create' }
        },
        {
          path: 'sns/cigars/:id/edit',
          name: 'CigarEdit',
          component: () => import('@/views/sns/cigars/cigar/edit.vue'),
          meta: { title: '编辑雪茄', permission: 'sns:cigar:edit' }
        },
        {
          path: 'sns/cigars/import',
          name: 'CigarImport',
          component: () => import('@/views/sns/cigars/import/index.vue'),
          meta: { title: '雪茄数据导入', permission: 'sns:cigar:create' }
        },
        {
          path: 'sns/cigars/import/tasks',
          name: 'CigarImportTasks',
          component: () => import('@/views/sns/cigars/import/tasks.vue'),
          meta: { title: '雪茄导入任务', permission: 'sns:cigar:read' }
        },
        {
          path: 'sns/appraiser',
          name: 'AppraiserList',
          component: () => import('@/views/sns/cigars/appraiser/index.vue'),
          meta: { title: '鉴茄师管理', permission: 'sns:appraiser:read' }
        },
        {
          path: 'sns/appraiser/:id/edit',
          name: 'AppraiserEdit',
          component: () => import('@/views/sns/cigars/appraiser/edit.vue'),
          meta: { title: '编辑鉴茄师', permission: 'sns:appraiser:edit' }
        },
        {
          path: 'sns/appraiser/application',
          name: 'AppraiserApplication',
          component: () => import('@/views/sns/cigars/appraiser/application/list.vue'),
          meta: { title: '鉴茄师审核', permission: 'sns:appraiser:application' }
        },
        {
          path: 'sns/appraiser/application/:id/review',
          name: 'AppraiserApplicationReview',
          component: () => import('@/views/sns/cigars/appraiser/application/review.vue'),
          meta: { title: '审核鉴茄师申请', permission: 'sns:appraiser:application' }
        },
        {
          path: 'sns/appraiser/application/:id/view',
          name: 'AppraiserApplicationView',
          component: () => import('@/views/sns/cigars/appraiser/application/view.vue'),
          meta: { title: '查看鉴茄师申请', permission: 'sns:appraiser:application' }
        },
        {
          path: 'sns/appraiser/category',
          name: 'AppraisalCategoryList',
          component: () => import('@/views/sns/cigars/appraiser/category/index.vue'),
          meta: { title: '鉴茄类别管理', permission: 'sns:appraisalCategory:read' }
        },
        {
          path: 'sns/appraiser/category/create',
          name: 'AppraisalCategoryCreate',
          component: () => import('@/views/sns/cigars/appraiser/category/create.vue'),
          meta: { title: '新建鉴茄类别', permission: 'sns:appraisalCategory:create' }
        },
        {
          path: 'sns/appraiser/category/:id/edit',
          name: 'AppraisalCategoryEdit',
          component: () => import('@/views/sns/cigars/appraiser/category/edit.vue'),
          meta: { title: '编辑鉴茄类别', permission: 'sns:appraisalCategory:edit' }
        },
        {
          path: 'sns/appraiser/category/indicator/create',
          name: 'AppraisalIndicatorCreate',
          component: () => import('@/views/sns/cigars/appraiser/category/indicator-create.vue'),
          meta: { title: '新建鉴茄指标', permission: 'sns:appraisalCategory:create' }
        },
        {
          path: 'sns/appraiser/category/indicator/:id/edit',
          name: 'AppraisalIndicatorEdit',
          component: () => import('@/views/sns/cigars/appraiser/category/indicator-edit.vue'),
          meta: { title: '编辑鉴茄指标', permission: 'sns:appraisalCategory:edit' }
        },

        {
          path: 'sns/sommelier',
          name: 'SommelierList',
          component: () => import('@/views/sns/cigars/sommelier/index.vue'),
          meta: { title: '侍茄师管理', permission: 'sns:sommelier:read' }
        },
        {
          path: 'sns/sommelier/level',
          name: 'SommelierLevelList',
          component: () => import('@/views/sns/cigars/sommelier/level/list.vue'),
          meta: { title: '侍茄师等级管理', permission: 'sns:sommelier:read' }
        },
        {
          path: 'sns/sommelier/level/create',
          name: 'SommelierLevelCreate',
          component: () => import('@/views/sns/cigars/sommelier/level/create.vue'),
          meta: { title: '新增侍茄师等级', permission: 'sns:sommelier:read' }
        },
        {
          path: 'sns/sommelier/level/:id/edit',
          name: 'SommelierLevelEdit',
          component: () => import('@/views/sns/cigars/sommelier/level/edit.vue'),
          meta: { title: '编辑侍茄师等级', permission: 'sns:sommelier:read' }
        },
        {
          path: 'sns/sommelier/:id/upgrade',
          name: 'SommelierUpgrade',
          component: () => import('@/views/sns/cigars/sommelier/upgrade.vue'),
          meta: { title: '侍茄师等级变更', permission: 'sns:sommelier:read' }
        },
        {
          path: 'sns/tags',
          name: 'TagList',
          component: () => import('@/views/sns/tags/list.vue'),
          meta: { title: '标签管理', permission: 'system:tag:read' }
        },
        {
          path: 'sns/tags/create',
          name: 'TagCreate',
          component: () => import('@/views/sns/tags/create.vue'),
          meta: { title: '新增标签', permission: 'system:tag:manage' }
        },
        {
          path: 'sns/tags/:id/edit',
          name: 'TagEdit',
          component: () => import('@/views/sns/tags/edit.vue'),
          meta: { title: '编辑标签', permission: 'system:tag:manage' }
        },
        {
          path: 'sns/tags/governance',
          name: 'TagGovernance',
          component: () => import('@/views/sns/tags/governance.vue'),
          meta: { title: '标签治理', permission: 'system:tag:manage' }
        },
        {
          path: 'sns/official-verify',
          name: 'OfficialVerifyList',
          component: () => import('@/views/sns/officialVerify/list.vue'),
          meta: { title: '官方认证审核', permission: 'sns:officialVerify:read' }
        },
        {
          path: 'sns/official-verify/:id/review',
          name: 'OfficialVerifyReview',
          component: () => import('@/views/sns/officialVerify/review.vue'),
          meta: { title: '审核官方认证申请', permission: 'sns:officialVerify:read' }
        },
        {
          path: 'sns/official-account',
          name: 'OfficialAccountList',
          component: () => import('@/views/sns/officialAccount/index.vue'),
          meta: { title: '官方号管理', permission: 'sns:officialAccount:manage' }
        },
        {
          path: 'sns/sommelier/application',
          name: 'SommelierApplication',
          component: () => import('@/views/sns/cigars/sommelier/application/list.vue'),
          meta: { title: '侍茄师审核', permission: 'sns:sommelier:application' }
        },
        {
          path: 'sns/sommelier/application/:id/review',
          name: 'SommelierApplicationReview',
          component: () => import('@/views/sns/cigars/sommelier/application/review.vue'),
          meta: { title: '审核侍茄师申请', permission: 'sns:sommelier:application' }
        },
        {
          path: 'sns/sommelier/application/:id/view',
          name: 'SommelierApplicationView',
          component: () => import('@/views/sns/cigars/sommelier/application/view.vue'),
          meta: { title: '查看侍茄师申请', permission: 'sns:sommelier:application' }
        },
        {
          path: 'sns/question',
          name: 'CigarQuestionList',
          component: () => import('@/views/sns/question/list.vue'),
          meta: { title: '题目管理', permission: 'sns:question:read' }
        },
        {
          path: 'sns/question/create',
          name: 'CigarQuestionCreate',
          component: () => import('@/views/sns/question/create.vue'),
          meta: { title: '新增题目', permission: 'sns:question:create' }
        },
        {
          path: 'sns/question/:id/edit',
          name: 'CigarQuestionEdit',
          component: () => import('@/views/sns/question/edit.vue'),
          meta: { title: '编辑题目', permission: 'sns:question:edit' }
        },
        {
          path: 'sns/topics',
          name: 'TopicList',
          component: () => import('@/views/sns/topic/index.vue'),
          meta: { title: '话题管理', permission: 'sns:note:audit' }
        },
        {
          path: 'sns/topics/create',
          name: 'TopicCreate',
          component: () => import('@/views/sns/topic/create.vue'),
          meta: { title: '新增话题', permission: 'sns:note:audit' }
        },
        {
          path: 'sns/topics/:id/edit',
          name: 'TopicEdit',
          component: () => import('@/views/sns/topic/edit.vue'),
          meta: { title: '编辑话题', permission: 'sns:note:audit' }
        },
   
        {
          path: 'system/users',
          name: 'UserList',
          component: () => import('@/views/system/user/index.vue'),
          meta: { title: '用户管理', permission: 'system:user:read' }
        },
        {
          path: 'system/users/create',
          name: 'UserCreate',
          component: () => import('@/views/system/user/create.vue'),
          meta: { title: '创建用户', permission: 'system:user:create' }
        },
        {
          path: 'system/users/:id/edit',
          name: 'UserEdit',
          component: () => import('@/views/system/user/edit.vue'),
          meta: { title: '编辑用户', permission: 'system:user:edit' }
        },
        {
          path: 'system/roles',
          name: 'RoleList',
          component: () => import('@/views/system/role/index.vue'),
          meta: { title: '角色管理', permission: 'system:role:read' }
        },
        {
          path: 'system/roles/create',
          name: 'RoleCreate',
          component: () => import('@/views/system/role/create.vue'),
          meta: { title: '创建角色', permission: 'system:role:create' }
        },
        {
          path: 'system/roles/:id/edit',
          name: 'RoleEdit',
          component: () => import('@/views/system/role/edit.vue'),
          meta: { title: '编辑角色', permission: 'system:role:edit' }
        },
        {
          path: 'system/roles/:id/permissions',
          name: 'RolePermissions',
          component: () => import('@/views/system/role/permission.vue'),
          meta: { title: '角色权限分配', permission: 'system:role:edit' }
        },
        {
          path: 'system/permissions',
          name: 'PermissionList',
          component: () => import('@/views/system/permission/index.vue'),
          meta: { title: '权限管理', permission: 'system:permission:read' }
        },
        {
          path: 'system/permissions/create',
          name: 'PermissionCreate',
          component: () => import('@/views/system/permission/create.vue'),
          meta: { title: '创建权限', permission: 'system:permission:create' }
        },
        {
          path: 'system/permissions/:id/edit',
          name: 'PermissionEdit',
          component: () => import('@/views/system/permission/edit.vue'),
          meta: { title: '编辑权限', permission: 'system:permission:edit' }
        },
        {
          path: 'system/app-menus',
          name: 'AppMenuList',
          component: () => import('@/views/system/appMenu/index.vue'),
          meta: { title: 'App 菜单管理', permission: 'app:menu:read' }
        },
        {
          path: 'system/departments',
          name: 'DepartmentList',
          component: () => import('@/views/system/department/index.vue'),
          meta: { title: '部门管理', permission: 'system:department:read' }
        },
        {
          path: 'system/departments/create',
          name: 'DepartmentCreate',
          component: () => import('@/views/system/department/create.vue'),
          meta: { title: '创建部门', permission: 'system:department:create' }
        },
        {
          path: 'system/departments/:id/edit',
          name: 'DepartmentEdit',
          component: () => import('@/views/system/department/edit.vue'),
          meta: { title: '编辑部门', permission: 'system:department:edit' }
        },
        {
          path: 'system/divisions',
          name: 'DivisionList',
          component: () => import('@/views/system/division/index.vue'),
          meta: { title: '行政区划管理', permission: 'system:division:read' }
        },
        {
          path: 'system/divisions/create',
          name: 'DivisionCreate',
          component: () => import('@/views/system/division/create.vue'),
          meta: { title: '创建行政区划', permission: 'system:division:create' }
        },
        {
          path: 'system/divisions/:id/edit',
          name: 'DivisionEdit',
          component: () => import('@/views/system/division/edit.vue'),
          meta: { title: '编辑行政区划', permission: 'system:division:edit' }
        },
        {
          path: 'system/countries',
          name: 'CountryList',
          component: () => import('@/views/system/country/index.vue'),
          meta: { title: '国家管理', permission: 'system:country:read' }
        },
        {
          path: 'system/countries/create',
          name: 'CountryCreate',
          component: () => import('@/views/system/country/create.vue'),
          meta: { title: '创建国家', permission: 'system:country:create' }
        },
        {
          path: 'system/countries/:id/edit',
          name: 'CountryEdit',
          component: () => import('@/views/system/country/edit.vue'),
          meta: { title: '编辑国家', permission: 'system:country:edit' }
        },
        {
          path: 'system/message/template',
          name: 'MessageTemplateList',
          component: () => import('@/views/system/messageTemplate/index.vue'),
          meta: { title: '消息模板管理', permission: 'system:messageTemplate:read' }
        },
        {
          path: 'system/message/template/create',
          name: 'MessageTemplateCreate',
          component: () => import('@/views/system/messageTemplate/create.vue'),
          meta: { title: '创建消息模板', permission: 'system:messageTemplate:create' }
        },
        {
          path: 'system/message/template/:id/edit',
          name: 'MessageTemplateEdit',
          component: () => import('@/views/system/messageTemplate/edit.vue'),
          meta: { title: '编辑消息模板', permission: 'system:messageTemplate:edit' }
        },
        {
          path: 'system/dict',
          name: 'DictList',
          component: () => import('@/views/system/dict/index.vue'),
          meta: { title: '数据字典管理', permission: 'system:dict:read' }
        },
        {
          path: 'system/dict/create',
          name: 'DictCreate',
          component: () => import('@/views/system/dict/create.vue'),
          meta: { title: '创建字典', permission: 'system:dict:create' }
        },
        {
          path: 'system/dict/:id/edit',
          name: 'DictEdit',
          component: () => import('@/views/system/dict/edit.vue'),
          meta: { title: '编辑字典', permission: 'system:dict:edit' }
        },
        {
          path: 'system/policy',
          name: 'PolicyList',
          component: () => import('@/views/system/policy/list.vue'),
          meta: { title: '政策条款管理', permission: 'system:policy:read' }
        },
        {
          path: 'system/policy/create',
          name: 'PolicyCreate',
          component: () => import('@/views/system/policy/create.vue'),
          meta: { title: '创建政策条款', permission: 'system:policy:create' }
        },
        {
          path: 'system/policy/:id/edit',
          name: 'PolicyEdit',
          component: () => import('@/views/system/policy/edit.vue'),
          meta: { title: '编辑政策条款', permission: 'system:policy:edit' }
        },
        {
          path: 'system/report/categories',
          name: 'ReportCategoryList',
          component: () => import('@/views/system/report/category/index.vue'),
          meta: { title: '举报分类管理', permission: 'system:report_category:read' }
        },
        {
          path: 'system/report/categories/create',
          name: 'ReportCategoryCreate',
          component: () => import('@/views/system/report/category/create.vue'),
          meta: { title: '新建举报分类', permission: 'system:report_category:create' }
        },
        {
          path: 'system/report/categories/:id/edit',
          name: 'ReportCategoryEdit',
          component: () => import('@/views/system/report/category/edit.vue'),
          meta: { title: '编辑举报分类', permission: 'system:report_category:edit' }
        },
        {
          path: 'system/report/records',
          name: 'ReportRecordList',
          component: () => import('@/views/system/report/record/index.vue'),
          meta: { title: '举报记录', permission: 'system:report_record:read' }
        },
        {
          path: 'store/categories',
          name: 'StoreCategories',
          component: () => import('@/views/store/category/index.vue'),
          meta: { title: '商品类目', permission: 'store:category:read' }
        },
        {
          path: 'store/categories/create',
          name: 'StoreCategoryCreate',
          component: () => import('@/views/store/category/create.vue'),
          meta: { title: '新增分类', permission: 'store:category:create' }
        },
        {
          path: 'store/categories/:id/edit',
          name: 'StoreCategoryEdit',
          component: () => import('@/views/store/category/edit.vue'),
          meta: { title: '编辑分类', permission: 'store:category:edit' }
        },
        {
          path: 'store/products',
          name: 'StoreProducts',
          component: () => import('@/views/store/product/index.vue'),
          meta: { title: '商品管理', permission: 'store:product:read' }
        },
        {
          path: 'store/products/create',
          name: 'StoreProductCreate',
          component: () => import('@/views/store/product/create.vue'),
          meta: { title: '创建商品', permission: 'store:product:edit' }
        },
        {
          path: 'store/products/:id/edit',
          name: 'StoreProductEdit',
          component: () => import('@/views/store/product/edit.vue'),
          meta: { title: '编辑商品', permission: 'store:product:edit' }
        },
        {
          path: 'store/products/:id/view',
          name: 'StoreProductView',
          component: () => import('@/views/store/product/view.vue'),
          meta: { title: '查看商品', permission: 'store:product:read' }
        },
        {
          path: 'store/attributes',
          name: 'StoreAttributes',
          component: () => import('@/views/store/attribute/index.vue'),
          meta: { title: '商品属性', permission: 'store:attribute:read' }
        },
        {
          path: 'store/attributes/create',
          name: 'StoreAttributeCreate',
          component: () => import('@/views/store/attribute/create.vue'),
          meta: { title: '创建属性', permission: 'store:attribute:create' }
        },
        {
          path: 'store/attributes/:id/edit',
          name: 'StoreAttributeEdit',
          component: () => import('@/views/store/attribute/edit.vue'),
          meta: { title: '编辑属性', permission: 'store:attribute:edit' }
        },
        {
          path: 'store/options',
          name: 'StoreOptions',
          component: () => import('@/views/store/option/index.vue'),
          meta: { title: '商品规格', permission: 'store:option:read' }
        },
        {
          path: 'store/options/create',
          name: 'StoreOptionCreate',
          component: () => import('@/views/store/option/create.vue'),
          meta: { title: '创建规格', permission: 'store:option:create' }
        },
        {
          path: 'store/options/:id/edit',
          name: 'StoreOptionEdit',
          component: () => import('@/views/store/option/edit.vue'),
          meta: { title: '编辑规格', permission: 'store:option:edit' }
        },
        {
          path: 'store/deliveryTemplate',
          name: 'FreightTemplateList',
          component: () => import('@/views/store/deliveryTemplate/index.vue'),
          meta: { title: '运费模板管理', permission: 'store:deliveryTemplate:read' }
        },
        {
          path: 'store/deliveryTemplate/create',
          name: 'FreightTemplateCreate',
          component: () => import('@/views/store/deliveryTemplate/create.vue'),
          meta: { title: '新增运费模板', permission: 'store:deliveryTemplate:create' }
        },
        {
          path: 'store/deliveryTemplate/:id/edit',
          name: 'FreightTemplateEdit',
          component: () => import('@/views/store/deliveryTemplate/edit.vue'),
          meta: { title: '编辑运费模板', permission: 'store:deliveryTemplate:edit' }
        },
        {
          path: 'store/orders',
          name: 'StoreOrderList',
          component: () => import('@/views/store/order/list.vue'),
          meta: { title: '订单管理', permission: 'store:order:read' }
        },
        {
          path: 'sns/sommelier/orders',
          name: 'SommelierOrderList',
          component: () => import('@/views/sns/cigars/sommelier/order/list.vue'),
          meta: { title: '侍茄师订单列表', permission: 'sns:sommelierOrder:read' }
        },
        {
          path: 'store/order/consign/:id',
          name: 'StoreOrderConsign',
          component: () => import('@/views/store/order/consign.vue'),
          meta: { title: '订单发货', permission: 'store:order:edit', hideInMenu: true }
        },
        {
          path: 'store',
          name: 'StoreList',
          component: () => import('@/views/store/store/list.vue'),
          meta: { title: '商家管理', permission: 'store:store:read' }
        },
        {
          path: 'store/:id/edit',
          name: 'StoreEdit',
          component: () => import('@/views/store/store/edit.vue'),
          meta: { title: '编辑商家', permission: 'store:store:edit' }
        },
        {
          path: 'store/application',
          name: 'StoreApplicationList',
          component: () => import('@/views/store/application/list.vue'),
          meta: { title: '商家入驻申请', permission: 'store:application:review' }
        },
        {
          path: 'store/application/:id/view',
          name: 'StoreApplicationView',
          component: () => import('@/views/store/application/view.vue'),
          meta: { title: '查看商家入驻申请', permission: 'store:application:review' }
        },
        {
          path: 'store/application/:id/review',
          name: 'StoreApplicationReview',
          component: () => import('@/views/store/application/review.vue'),
          meta: { title: '审核商家入驻申请', permission: 'store:application:review' }
        },
 
        {
          path: 'marketing/coupon',
          name: 'StoreCouponList',
          component: () => import('@/views/store/coupon/index.vue'),
          meta: { title: '优惠券管理', permission: 'marketing:coupon:read' }
        },
        {
          path: 'marketing/coupon/create',
          name: 'StoreCouponCreate',
          component: () => import('@/views/store/coupon/create.vue'),
          meta: { title: '新建优惠券', permission: 'marketing:coupon:create' }
        },
        {
          path: 'marketing/coupon/:id/edit',
          name: 'StoreCouponEdit',
          component: () => import('@/views/store/coupon/edit.vue'),
          meta: { title: '编辑优惠券', permission: 'marketing:coupon:edit' }
        },
        {
          path: 'marketing/wishlist',
          name: 'WishlistList',
          component: () => import('@/views/marketing/wishlist/index.vue'),
          meta: { title: '愿望单管理', permission: 'marketing:wishlist:read' }
        },
        {
          path: 'marketing/virtual-item',
          name: 'VirtualItemList',
          component: () => import('@/views/sns/virtualItem/list.vue'),
          meta: { title: '虚拟道具列表', permission: 'marketing:virtualItem:read' }
        },
        {
          path: 'marketing/virtual-item/create',
          name: 'VirtualItemCreate',
          component: () => import('@/views/sns/virtualItem/create.vue'),
          meta: { title: '新增虚拟道具', permission: 'marketing:virtualItem:create' }
        },
        {
          path: 'marketing/virtual-item/:id/edit',
          name: 'VirtualItemEdit',
          component: () => import('@/views/sns/virtualItem/edit.vue'),
          meta: { title: '编辑虚拟道具', permission: 'marketing:virtualItem:edit' }
        },
        {
          path: 'marketing/checkin/rules',
          name: 'CheckinRewardRuleList',
          component: () => import('@/views/sns/checkin/rules/list.vue'),
          meta: { title: '签到奖励规则', permission: 'marketing:checkin:read' }
        },
        {
          path: 'marketing/checkin/rules/create',
          name: 'CheckinRewardRuleCreate',
          component: () => import('@/views/sns/checkin/rules/create.vue'),
          meta: { title: '创建签到奖励规则', permission: 'marketing:checkin:create' }
        },
        {
          path: 'marketing/checkin/rules/:id/edit',
          name: 'CheckinRewardRuleEdit',
          component: () => import('@/views/sns/checkin/rules/edit.vue'),
          meta: { title: '编辑签到奖励规则', permission: 'marketing:checkin:edit' }
        },
        {
          path: 'sns/appraiser/orders',
          name: 'AppraiserOrderList',
          component: () => import('@/views/sns/cigars/appraiser/order/list.vue'),
          meta: { title: '鉴茄师订单列表', permission: 'sns:appraiserOrder:read' }
        },
        {
          path: 'test-upload',
          name: 'TestUpload',
          component: () => import('@/views/test-upload.vue'),
          meta: { title: '上传测试', permission: 'store:product:read' }
        },
        {
          path: 'sns/notes',
          name: 'NoteList',
          component: () => import('@/views/sns/note/index.vue'),
          meta: { title: '帖子管理', permission: 'sns:note:read' }
        },
        {
          path: 'sns/notes/create',
          name: 'NoteCreate',
          component: () => import('@/views/sns/note/create.vue'),
          meta: { title: '新增帖子', permission: 'sns:note:create' }
        },
        {
          path: 'sns/notes/:id/edit',
          name: 'NoteEdit',
          component: () => import('@/views/sns/note/edit.vue'),
          meta: { title: '编辑帖子', permission: 'sns:note:edit' }
        },
        {
          path: 'sns/notes/templates',
          name: 'NoteTemplateList',
          component: () => import('@/views/sns/note/template/list.vue'),
          meta: { title: '帖子模板', permission: 'sns:noteTemplate:read' }
        },
        {
          path: 'sns/notes/templates/create',
          name: 'NoteTemplateCreate',
          component: () => import('@/views/sns/note/template/create.vue'),
          meta: { title: '新建模板', permission: 'sns:noteTemplate:manage' }
        },
        {
          path: 'sns/notes/templates/:id/edit',
          name: 'NoteTemplateEdit',
          component: () => import('@/views/sns/note/template/edit.vue'),
          meta: { title: '编辑模板', permission: 'sns:noteTemplate:manage' }
        },
        {
          path: 'sns/fonts',
          name: 'FontAssetList',
          component: () => import('@/views/sns/font/list.vue'),
          meta: { title: '字体管理', permission: 'sns:fontAsset:read' }
        },
        {
          path: 'sns/notes/audit',
          name: 'NoteReviewIndex',
          component: () => import('@/views/sns/audit/note/index.vue'),
          meta: { title: '帖子审核', permission: 'audit:note' }
        },
        {
          path: 'sns/notes/audit/:id',
          name: 'NoteReviewDetail',
          component: () => import('@/views/sns/audit/note/review.vue'),
          meta: { title: '帖子审核详情', permission: 'audit:note', hideInMenu: true }
        },
        {
          path: 'sns/comments/audit',
          name: 'CommentReviewIndex',
          component: () => import('@/views/sns/audit/comment/index.vue'),
          meta: { title: '评论审核', permission: 'audit:comment' }
        },
        {
          path: 'sns/comments/audit/:id',
          name: 'CommentReviewDetail',
          component: () => import('@/views/sns/audit/comment/review.vue'),
          meta: { title: '评论审核详情', permission: 'audit:comment', hideInMenu: true }
        },
        {
          path: 'fund/bill',
          name: 'FundBill',
          component: () => import('@/views/fund/bill/index.vue'),
          meta: { title: '账单管理', permission: 'fund:bill:read' }
        },
        {
          path: 'fund/deposit',
          name: 'FundDeposit',
          component: () => import('@/views/fund/deposit/index.vue'),
          meta: { title: '保证金账户', permission: 'fund:deposit:read' }
        },
        {
          path: 'fund/invoice',
          name: 'FundInvoice',
          component: () => import('@/views/fund/invoice/index.vue'),
          meta: { title: '发票管理', permission: 'fund:invoice:read' }
        },
        {
          path: 'system/pay/config',
          name: 'PayChannelConfig',
          component: () => import('@/views/system/pay/config.vue'),
          meta: { title: '支付方式设置', permission: 'pay:channel:read' }
        },
        {
          path: 'system/lexiconManage',
          name: 'SensitiveWordList',
          component: () => import('@/views/system/sensitiveWord/index.vue'),
          meta: { title: '词库管理', permission: 'system:sensitiveWord:read' }
        },
        {
          path: 'system/lexiconManage/create',
          name: 'SensitiveWordCreate',
          component: () => import('@/views/system/sensitiveWord/create.vue'),
          meta: { title: '新增敏感词', permission: 'system:sensitiveWord:create', hideInMenu: true }
        },
        {
          path: 'system/lexiconManage/:id/edit',
          name: 'SensitiveWordEdit',
          component: () => import('@/views/system/sensitiveWord/edit.vue'),
          meta: { title: '编辑敏感词', permission: 'system:sensitiveWord:edit', hideInMenu: true }
        },
        {
          path: 'marketing/emoji',
          name: 'EmojiList',
          component: () => import('@/views/sns/emoji/tabs/list.vue'),
          meta: { title: '表情页签管理', permission: 'marketing:emoji:read' }
        },
        {
          path: 'marketing/emoji/tabs/create',
          name: 'EmojiCreate',
          component: () => import('@/views/sns/emoji/tabs/create.vue'),
          meta: { title: '新增表情页签', permission: 'marketing:emoji:create' }
        },
        {
          path: 'marketing/emoji/tabs/:id/edit',
          name: 'EmojiEdit',
          component: () => import('@/views/sns/emoji/tabs/edit.vue'),
          meta: { title: '编辑表情页签', permission: 'marketing:emoji:edit' }
        },
        {
          path: 'marketing/emoji/tuples/create',
          name: 'EmojiTupleCreate',
          component: () => import('@/views/sns/emoji/tuples/create.vue'),
          meta: { title: '创建表情项', permission: 'marketing:emoji:create' }
        },
        {
          path: 'marketing/emoji/tuples/:id/edit',
          name: 'EmojiTupleEdit',
          component: () => import('@/views/sns/emoji/tuples/edit.vue'),
          meta: { title: '编辑表情项', permission: 'marketing:emoji:edit' }
        },

        {
          path: 'vip/souvenirs',
          name: 'VipSouvenirList',
          component: () => import('@/views/vip/souvenir/index.vue'),
          meta: { title: '纪念品发放', permission: 'vip:souvenir:read' }
        },

        // ============ 客服模块 ============
        {
          path: 'cs/workbench',
          name: 'CsWorkbench',
          component: () => import('@/views/cs/workbench/index.vue'),
          meta: { title: '客服工作台', permission: 'cs:agent:read' }
        },
        {
          path: 'cs/tickets',
          name: 'CsTicketList',
          component: () => import('@/views/cs/ticket/index.vue'),
          meta: { title: '工单管理', permission: 'cs:ticket:read' }
        },
        {
          path: 'cs/tickets/:id',
          name: 'CsTicketDetail',
          component: () => import('@/views/cs/ticket/detail.vue'),
          meta: { title: '工单详情', permission: 'cs:ticket:read', hideInMenu: true }
        },
        {
          path: 'cs/faqs',
          name: 'CsFaqList',
          component: () => import('@/views/cs/faq/index.vue'),
          meta: { title: 'FAQ 知识库', permission: 'cs:faq:read' }
        },
        {
          path: 'cs/agents',
          name: 'CsAgentList',
          component: () => import('@/views/cs/agent/index.vue'),
          meta: { title: '坐席管理', permission: 'cs:agent:manage' }
        },
        {
          path: 'cs/business-hours',
          name: 'CsBusinessHourList',
          component: () => import('@/views/cs/businessHour/index.vue'),
          meta: { title: '营业时间', permission: 'cs:config:read' }
        }
        // 注：账户注销是 C 端 oc_customer 的自助能力，仅在移动 App 暴露；
        // 管理后台 (sys_user) 不应出现该入口，B 端用户被禁用走「系统管理 → 用户管理」即可。
      ]
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'NotFound',
      component: () => import('@/views/NotFound.vue'),
      meta: { requiresAuth: false, title: '404' }
    }
  ]
})

// Global navigation guard
router.beforeEach(async (to, from, next) => {
  // Start progress bar
  NProgress.start()

  const authStore = useAuthStore()

  // 页面标题：后台页面带「后台管理」后缀，公开页面带「雅莳」后缀
  const isAdminRoute = to.path === '/admin' || to.path.startsWith('/admin/')
  document.title = `${to.meta.title || '首页'} - ${isAdminRoute ? '后台管理' : '雅莳'}`
  
  try {
    // 用户信息不再持久化到 localStorage：已登录但内存中无 user 时（如硬刷新），
    // 通过 /me 重新拉取，保证权限实时；拉取失败视为登录态失效，进入下方 catch 跳登录。
    if (authStore.isAuthenticated && !authStore.user) {
      await authStore.fetchCurrentUser()
    }

    const requiresAuth = to.matched.some(record => record.meta.requiresAuth !== false)
    // 权限项可能缺少 code（如旧版本本地缓存的用户数据），必须做空值保护，
    // 否则 p.code.toLowerCase() 会抛错导致守卫进入 catch，首次导航被取消后整页空白。
    const requiredPermission = to.meta.permission as string | undefined
    const hasPermission = requiredPermission
      ? (authStore.user?.permissions?.some(
          p => p?.code?.toLowerCase() === requiredPermission.toLowerCase()
        ) ?? false)
      : true

    // Handle authentication
    if (requiresAuth && !authStore.isAuthenticated) {
      message.warning('请先登录')
      next({ 
        name: 'Login', 
        query: { redirect: to.fullPath },
        replace: true 
      })
      return
    }

    // Handle already authenticated users trying to access login/register
    if ((to.name === 'Login' || to.name === 'Register') && authStore.isAuthenticated) {
      next({ name: 'Dashboard', replace: true })
      return
    }

    // Handle permissions
    if (requiresAuth && !hasPermission) {
      message.error('没有访问权限')
      // 已登录却无权限，通常是本地缓存的登录态不完整/已失效。
      // 必须先清除登录态再跳登录页，否则登录页会因「已登录」再次重定向到仪表盘，
      // 与此处形成「登录页 ↔ 仪表盘」死循环导致整页空白。
      authStore.logout()
      next({ 
        name: 'Login', 
        query: { redirect: to.fullPath },
        replace: true 
      })
      return
    }

    // Proceed with navigation
    next()
  } catch (error) {
    console.error('Navigation error:', error)
    message.error('页面加载失败')
    // 守卫异常（含 /me 拉取失败）通常意味着登录态已失效，必须先清除登录态，
    // 否则 token 仍在会导致登录页再次跳回仪表盘，与此处形成死循环、整页空白。
    authStore.logout()
    // 不能使用 next(false)：首次导航被取消会让 router-view 无内容、整页空白。
    // 守卫异常时安全跳转到登录页，保证始终有可渲染的视图。
    if (to.name !== 'Login') {
      next({ name: 'Login', query: { redirect: to.fullPath }, replace: true })
    } else {
      next()
    }
  }
})

router.afterEach(() => {
  // Complete progress bar
  NProgress.done()
})

// Handle navigation errors
router.onError((error) => {
  console.error('Router error:', error)
  NProgress.done()
  message.error('页面加载失败')
})

export default router